import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api/Client";
import { SecureStorage } from "../storage/SecureStorage";
import {API_BASE} from "../config.ts";

/** ==== Tipos ==== */
type ServiceIncluded = { id: number; name: string };
type DetailResponse = {
  name: string;
  image?: string | null;
  description?: string | null;
  created_at?: string | null;
  expiry_date?: string | null;
  amount?: number | null;
  discount?: number | null;
  total_amount?: number | null;
  payment_method?: string | null;
  payment_status?: string | null;
  invoice_number?: string | null;
  centro?: string | null;
  type_of_product?: "single_session" | "multi_sessions" | "recurrent" | string | null;
  total_session?: number | null;
  sessions_remaining?: number | null;
  sessions_consumed?: number | null;
  weekly_limit?: number | null;
  services: ServiceIncluded[];
};

const EUR = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

/** ==== Utils ==== */
function toDisplayType(t?: string | null) {
  if (!t) return "—";
  if (t === "single_session") return "SINGLE";
  if (t === "multi_sessions") return "BONO";
  if (t === "recurrent") return "RECURRENTE";
  return t.toUpperCase();
}
function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}-${m}-${y}`;
}
// function formatPaymentMethod(s?: string | null) { return s ? s.toUpperCase() : "—"; }
//function formatPaymentStatus(s?: string | null) { return s ? s.toUpperCase() : "—"; }

/** ==== Página ==== */
export default function ProductDetailPage() {
  const { productId, activeProductId } = useParams<{ productId: string; activeProductId: string }>();
  const navigate = useNavigate();
  const productIdNum = productId ? parseInt(productId, 10) : 0;
  const location = useLocation() as { state?: { activeProductId?: number } };
  const activeProductIdFromState = location?.state?.activeProductId ?? undefined;
  const activeProductIdNum = activeProductId ? parseInt(activeProductId, 10) : 0;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailResponse | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const user = await SecureStorage.getUser();
        if (!user?.id || !productIdNum) throw new Error("Producto no disponible");
        const params: any = { user_id: user.id, product_id: productIdNum, active_product_id: activeProductIdNum };
        if (activeProductIdFromState) params.active_product_id = activeProductIdFromState;
        const r = await api.get<DetailResponse>("/api/mobile/product-details", { params });
        setDetail(r.data);
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || "No se pudieron cargar los detalles");
      } finally {
        setLoading(false);
      }
    })();
  }, [productIdNum]);

  const heroTitle = useMemo(() => (detail?.name || "Producto"), [detail?.name]);
  const priceBox = useMemo(() => {
    const base = EUR.format(detail?.amount ?? 0);
    const total = EUR.format(detail?.total_amount ?? detail?.amount ?? 0);
    const hasDiscount = (detail?.discount ?? 0) > 0 && (detail?.total_amount ?? 0) < (detail?.amount ?? 0);
    return { base, total, hasDiscount };
  }, [detail]);

  return (
    <div style={pageOuter}>
      {/* Hero compacto */}
      <div style={heroWrap}>
        <div style={heroMedia}>
          {detail?.image ? (
            <img
              src={`${API_BASE}/api/product_images/${detail.image}`}
              alt={heroTitle}
              style={heroImg}
            />
          ) : (
            <div style={heroImgFallback}>🏷️</div>
          )}
          <div style={heroBadgesRow}>
            <span style={chipPrimary}>{toDisplayType(detail?.type_of_product)}</span>
          </div>
        </div>

        <div style={heroInfo}>
          <h1 style={h1}>{heroTitle.toUpperCase()}</h1>
          {/* Precio */}
          <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
            {priceBox.hasDiscount && (
              <span style={priceStriked}>{priceBox.base}</span>
            )}
            <span style={priceStrong}>{priceBox.total}</span>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div style={contentWrap}>
        {/* Mi compra */}
        <section style={card}>
          <SectionTitle icon="🧾" title="Mi compra" />
          <div style={kvGrid}>
            <KV label="Fecha de compra" value={fmtDate(detail?.created_at)} />
            <KV label="Caduca" value={fmtDate(detail?.expiry_date)} />
            <KV label="Centro" value={detail?.centro ? String(detail.centro).toUpperCase() : "—"} />
            <KV label="Precio" value={EUR.format(detail?.amount ?? 0)} />
            <KV label="Método" value={formatPaymentMethod(detail?.payment_method)} />
            <KV label="Estado" value={formatPaymentStatus(detail?.payment_status)} />
          </div>
          {detail?.description && <p style={descText}>{detail.description}</p>}
        </section>

        {/* Sesiones / Límite semanal */}
        <section style={card}>
          <SectionTitle icon="⏳" title="Sesiones" />
          {detail?.type_of_product === "recurrent" ? (
            <div>
              <div style={bigMetricRow}>
                <span style={metricLabel}>Límite semanal</span>
                <span style={metricValue}>{detail?.weekly_limit ?? 0}/semana</span>
              </div>
              <p style={muted}>Este producto renueva por semanas. Las sesiones no “se acumulan”.</p>
            </div>
          ) : (
            <div>
              <div style={bigMetricRow}>
                <span style={metricLabel}>Restantes</span>
                <span style={metricValue}>
                  {detail?.sessions_remaining ?? "—"}{typeof detail?.total_session === "number" ? ` / ${detail?.total_session}` : ""}
                </span>
              </div>
              {typeof detail?.sessions_remaining === "number" && typeof detail?.total_session === "number" && detail.total_session > 0 && (
                <Progress value={Math.min(100, ((detail.total_session - (detail.sessions_remaining ?? 0)) / detail.total_session) * 100)} />
              )}
            </div>
          )}
        </section>

        {/* Servicios incluidos */}
        <section style={card}>
          <SectionTitle icon="🧩" title="Servicios incluidos" />
          {detail?.services?.length ? (
            <div style={chipsWrap}>
              {detail.services.map(s => (
                <span key={s.id} style={chipService}>{s.name}</span>
              ))}
            </div>
          ) : (
            <p style={muted}>No hay servicios asociados.</p>
          )}
        </section>
        {/* CTA pegajoso */}
           <div style={{ marginTop: 20 }}>
            <button
              style={ctaBtnInline}
              onClick={() => navigate("/calendar", { state: activeProductIdFromState ? { productId: productIdNum, activeProductId: activeProductIdFromState } : { productId: productIdNum } })}
              aria-label="Reservar con este producto"
            >
              Reservar
            </button>
          </div>
        <div style={{ height: 96 }} /> {/* colchón adicional visual */}
      </div>
    </div>
  );
}

/** ==== Subcomponentes & estilos ==== */
function SectionTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <div aria-hidden style={sectionIcon}>{icon}</div>
      <div style={sectionTitleText}>{title}</div>
    </div>
  );
}
function KV({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div style={kvLabel}>{label}</div>
      <div style={kvValue}><strong>{String(value)}</strong></div>
    </div>
  );
}
function Progress({ value }: { value: number }) {
  return (
    <div style={progressOuter}>
      <div style={{ ...progressInner, width: `${value}%` }} />
    </div>
  );
}
function formatPaymentMethod(s?: string | null) {
  if (!s) return "—";
  switch (s.toLowerCase()) {
    case "cash": return "Efectivo";
    case "card": return "Tarjeta";
    case "bank_transfer": return "Transferencia bancaria";
    default: return s;
  }
}

function formatPaymentStatus(s?: string | null) {
  if (!s) return "—";
  switch (s.toLowerCase()) {
    case "paid": return "Pagado";
    case "pending": return "Pendiente";
    case "unpaid": return "No pagado";
    default: return s;
  }
}


/* ==== estilos (idénticos a tu versión anterior, los dejo igual) ==== */
const pageOuter: React.CSSProperties = { position: "relative", minHeight: "100dvh", paddingBottom: "calc(1rem + var(--app-footer-height, 72px) + env(safe-area-inset-bottom, 0px))", background: "#f3f4f6" };
const heroWrap: React.CSSProperties = { display: "grid", gridTemplateColumns: "120px 1fr", gap: 12, padding: "12px 12px 0" };
const heroMedia: React.CSSProperties = { position: "relative" };
const heroImg: React.CSSProperties = { width: "100%", height: 120, objectFit: "contain", borderRadius: 12, background: "#fff", border: "1px solid #e5e7eb" };
const heroImgFallback: React.CSSProperties = { width: "100%", height: 120, borderRadius: 12, background: "#fff", display: "grid", placeItems: "center", border: "1px solid #e5e7eb", fontSize: 28 };
const heroBadgesRow: React.CSSProperties = { position: "absolute", left: 6, bottom: 6, display: "flex", gap: 6, flexWrap: "wrap" };
const chipPrimary: React.CSSProperties = { background: "#1976d2", color: "#fff", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 800 };
const heroInfo: React.CSSProperties = { display: "flex", flexDirection: "column", justifyContent: "center" };
const h1: React.CSSProperties = { margin: 0, fontSize: 18, fontWeight: 800, color: "#111827" };
const priceStriked: React.CSSProperties = { textDecoration: "line-through", color: "#9ca3af", fontWeight: 600, fontSize: 14 };
const priceStrong: React.CSSProperties = { color: "#c62828", fontWeight: 800, fontSize: 20 };
const contentWrap: React.CSSProperties = { padding: 12, display: "grid", gap: 12, maxWidth: 760, margin: "0 auto" };
const card: React.CSSProperties = { background: "#fff", borderRadius: 12, padding: 14, border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(0,0,0,.04)" };
const descText: React.CSSProperties = { marginTop: 8, color: "#374151", lineHeight: 1.45 };
const sectionIcon: React.CSSProperties = { width: 36, height: 36, borderRadius: 8, display: "grid", placeItems: "center", background: "#f3f4f6", fontSize: 18 };
const sectionTitleText: React.CSSProperties = { fontWeight: 800, color: "#111827" };
const kvGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const kvLabel: React.CSSProperties = { fontSize: 12, color: "#6b7280" };
const kvValue: React.CSSProperties = { fontSize: 14, color: "#111827" };
const bigMetricRow: React.CSSProperties = { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 };
const metricLabel: React.CSSProperties = { color: "#6b7280", fontWeight: 700 };
const metricValue: React.CSSProperties = { fontWeight: 800, fontSize: 22, color: "#111827" };
const muted: React.CSSProperties = { color: "#6b7280", fontSize: 13 };
const progressOuter: React.CSSProperties = { height: 10, background: "#eee", borderRadius: 999, overflow: "hidden", border: "1px solid #e5e7eb" };
const progressInner: React.CSSProperties = { height: "100%", background: "#1976d2" };
const chipsWrap: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 8 };
const chipService: React.CSSProperties = { padding: "6px 10px", borderRadius: 999, background: "#e3f2fd", color: "#1d4ed8", border: "1px solid #cfe8ff", fontWeight: 700, fontSize: 12 };
//const stickyBar: React.CSSProperties = { position: "fixed", left: 0, right: 0, bottom: 0, background: "#fff", boxShadow: "0 -6px 20px rgba(0,0,0,.08)", padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))" };
//const ctaBtn: React.CSSProperties = { width: "100%", padding: "16px 14px", borderRadius: 999, background: "#1976d2", color: "#fff", borderWidth: 0, fontWeight: 800, fontSize: 18, cursor: "pointer" };
const ctaBtnInline: React.CSSProperties = {
  width: "100%",
  padding: "16px 14px",
  borderRadius: 999,
  background: "#d21c19ff",
  color: "#fff",
  borderWidth: 0,
  fontWeight: 800,
  fontSize: 18,
  cursor: "pointer",
};
const stickyBar: React.CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  background: "#fff",
  boxShadow: "0 -6px 20px rgba(0,0,0,.12)",
  padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
  zIndex: 1000, // ← clave
};

const ctaBtn: React.CSSProperties = {
  width: "100%",
  padding: "16px 14px",
  borderRadius: 999,
  background: "#1976d2",
  color: "#fff",
  borderWidth: 0,
  fontWeight: 800,
  fontSize: 18,
  cursor: "pointer",
};
